import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  override,
} from "mobx"

import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { TransactionsOutFilters } from "@src/pages/transactionsOut/constants"
import { PaymentsFilter } from "@src/pages/transactionsPayments/constants"
import { catchErrors, massPayoutsApi, paymentsApi } from "@src/shared/api"
import {
  NetworkCrypto,
  Pagination,
  Payment,
  SymbolCrypto,
} from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"

export type PaymentsLoadRequest = {
  address?: string
  amlLevel?: "high" | "low" | "medium"
  chargeId?: string
  id?: string
  network?: NetworkCrypto
  order?: "asc" | "desc"
  page?: number
  pricingType?: any
  status?: "confirmed" | "failed" | "pending"
  symbol?: SymbolCrypto
  txId?: string
}

export type PaymentsFilters = {}

export class PaymentsCollection extends loading(
  ["fetch"],
  CollectionWithPagination<Payment>,
) {
  private _currencies = GlobalCurrenciesCollection()

  constructor() {
    super()

    makeObservable(this, {
      fetch: action.bound,
      filter: computed,
      networks: computed,
      pagination: override,
      symbols: computed,
    })

    onBecomeObserved(this, "isLoading", this.load)
  }

  async fetch(req: PaymentsLoadRequest = {}) {
    try {
      const { page = 0 } = req

      const {
        data: { data },
      } = await paymentsApi.getPayments(
        req.order,
        this.calcOffset(page),
        this.pageSize,

        req.id,
        req.chargeId,
        req.pricingType,
        req.address,
        undefined,
        req.txId,
        req.network,
        req.symbol,
        req.status,
        req.amlLevel,
      )

      if (data) {
        return {
          data: data.data as Payment[],
          pagination: data.pagination as Pagination,
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }

    return null
  }

  get filter() {
    return new CollectionFilters<any>(
      [
        ...PaymentsFilter,
        CollectionFilters.createField("network").select(this.networks, {
          col: 6,
          label: "Network",
          placeholder: "Select a network",
        }),

        CollectionFilters.createField("symbol").select(this.symbols, {
          col: 6,
          label: "Symbol",
          placeholder: "Select a symbol",
        }),
      ],
      this.load,
    )
  }

  get networks() {
    return this._currencies.cryptoNetworks.map((i) => ({
      label: i,
      value: i,
    }))
  }

  get symbols() {
    return [...new Set(this._currencies.crypto.map((i) => i.symbol))].map(
      (i) => ({ label: i, value: i }),
    )
  }
}
